import { Watermark, Banner, ShareButton, OpenInBrowserButton, SignOutLink } from "@2skies/frontend-shared";
import { DeltaTapeHeader } from "./DeltaTapeHeader.js";
import { LeftPane } from "./LeftPane.js";
import { RightPane } from "./RightPane.js";
import { DeltaTapeFooter } from "./DeltaTapeFooter.js";
import type { TapeConnection } from "../hooks/useTapeStream.js";
import type { TickerTapeSnapshot } from "../types.js";

interface Props {
  symbols: string[];
  symbol: string;
  onSymbol: (symbol: string) => void;
  show0dte: boolean;
  onToggle0dte: (next: boolean) => void;
  snapshot: TickerTapeSnapshot | null;
  connection: TapeConnection;
  marketClosed: boolean;
  freshnessLabel: string;
  freshnessStale: boolean;
  username: string;
  /** True inside the Discord Activity iframe; false in a normal browser tab. */
  embedded: boolean;
  browserUrl: string;
}

/**
 * DeltaTape shell: watermark + header + two-pane body + footer, with a status
 * banner (market-closed / reconnecting) above the body. Presentational — all
 * live state is supplied by props (App wires the hooks) so the shell unit-tests
 * without mounting the chart.
 */
export function DeltaTapePane(props: Props): JSX.Element {
  const { snapshot, connection, marketClosed } = props;

  return (
    <div className="deltatape-pane">
      <Watermark />
      <DeltaTapeHeader
        symbols={props.symbols}
        symbol={props.symbol}
        onSymbol={props.onSymbol}
        show0dte={props.show0dte}
        onToggle0dte={props.onToggle0dte}
        share={
          props.embedded ? (
            <>
              <OpenInBrowserButton app="deltatape" browserUrl={props.browserUrl} />
              <ShareButton
                app="deltatape"
                brandLabel="DeltaTape"
                username={props.username}
                contextLine={props.symbol}
                symbol={props.symbol}
                target=".deltatape-body"
              />
            </>
          ) : (
            // Browser mode: Share needs Discord's in-app share dialog, so it's hidden here.
            <SignOutLink appPath="/api/deltatape" />
          )
        }
      />
      {marketClosed ? (
        <Banner variant="closed">market closed — resumes at next open</Banner>
      ) : connection === "error" ? (
        <Banner variant="degraded">reconnecting to the live tape…</Banner>
      ) : null}
      <div className="deltatape-body">
        {snapshot ? (
          <>
            <LeftPane symbol={props.symbol} pane={snapshot.pane} line={snapshot.line} show0dte={props.show0dte} />
            <RightPane pane={snapshot.pane} />
          </>
        ) : null}
      </div>
      <DeltaTapeFooter label={props.freshnessLabel} stale={props.freshnessStale} />
    </div>
  );
}
